// Where everything in the investigation's graph goes. The machines and their
// sessions are a tree of nodes down the left, with the people seen on each
// machine beside it; what happened in each session is marks on one clock in
// the session's lane, the new ones labelled where the label fits. Each claim
// is a numbered badge in a band over the lanes, at the time of what it rests
// on, with straight stems down to those marks; each host reached hangs under
// the lane of the processes that reached it, joined to them by short ticks,
// so no line crosses a lane it is not about. A stem always ends on something
// drawn. Pure, so positions, stems and the
// clock's breaks are tested without a browser; GraphTimeline draws it.

import { axisTicks } from "./graphTimeline.js";

export const LAYOUT = Object.freeze({
    axisH: 22,
    headH: 20,
    laneH: 26,
    labelH: 9,
    railMin: 110,
    railMax: 170,
    breakW: 22,
    // Marks closer than this merge into one, so a busy lane draws a bounded number.
    bucketPx: 4,
    undatedW: 32,
    // Machines reached with nothing in them get a lane each, up to this many.
    maxBare: 8,
    // A clock breaks into at most this many bursts; smaller gaps stay drawn.
    maxBursts: 6,
    // Lanes past this many are counted, not drawn.
    maxLanes: 60,
    badgeW: 14,
    badgeH: 12,
    badgeRowH: 16,
    maxBadgeRows: 3,
    // Badges for the claims the claims list shows.
    maxBadges: 12,
    // A lane grows a row for each host its processes reached, up to this many.
    hostRowH: 11,
    maxLaneHosts: 3,
    chipH: 13,
});

// Where each kind's marks sit in a lane, under its label band.
const MARK_Y = { message: 4, prompt: 9, process: 14 };
// Label widths are estimated from their length: the chart's labels are 9.5px mono.
export const CHAR_W = 5.7;
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const clip = (text, max) => {
    const s = String(text ?? "");
    return s.length > max ? `${s.slice(0, Math.max(1, max - 3))}...` : s;
};

// What a lane or machine with nothing in it says: which results named it.
export function namedText(named) {
    if (!named?.words) return "nothing in it yet";
    return `named by ${named.words}${named.step ? ` in step ${named.step}` : ""}`;
}

export function gapText(ms) {
    if (ms < HOUR) return `${Math.max(1, Math.round(ms / MINUTE))}m`;
    if (ms < 2 * DAY) return `${Math.round(ms / HOUR)}h`;
    return `${Math.round(ms / DAY)}d`;
}

/**
 * A clock that closes long empty stretches. The times the chart holds fall
 * into bursts; each burst gets width by how long it lasts (a short one still a
 * fair share), and the gaps between them shrink to a fixed break, so a burst
 * of work an hour long is not squeezed into a sliver of a day-wide axis.
 * Times in a gap map into its break, so spans and windows stay continuous.
 */
export function brokenScale(points, x0, x1, { breakW = LAYOUT.breakW, maxBursts = LAYOUT.maxBursts } = {}) {
    const times = [...new Set(points.filter(Number.isFinite))].sort((a, b) => a - b);
    if (!times.length || !(x1 > x0)) return null;
    const first = times[0];
    const last = times[times.length - 1];
    const gapMin = Math.max(20 * MINUTE, (last - first) * 0.12);
    // The widest gaps become breaks, at most maxBursts - 1 of them.
    const gaps = [];
    for (let i = 1; i < times.length; i++) if (times[i] - times[i - 1] > gapMin) gaps.push(i);
    const kept = new Set(gaps.sort((a, b) => (times[b] - times[b - 1]) - (times[a] - times[a - 1])).slice(0, maxBursts - 1));
    const bursts = [];
    let start = first;
    for (let i = 1; i < times.length; i++) {
        if (kept.has(i)) {
            bursts.push({ from: start, to: times[i - 1] });
            start = times[i];
        }
    }
    bursts.push({ from: start, to: last });
    const total = bursts.reduce((n, b) => n + (b.to - b.from), 0);
    const weights = bursts.map((b) => Math.max(b.to - b.from, total * 0.15, MINUTE));
    const breaks = bursts.length - 1;
    const room = Math.max(bursts.length * 12, x1 - x0 - breaks * breakW);
    const sum = weights.reduce((a, b) => a + b, 0);
    const intervals = [];
    let x = x0;
    bursts.forEach((b, i) => {
        const w = (room * weights[i]) / sum;
        intervals.push({ t0: b.from, t1: b.to, x0: x, x1: x + w, gap: false });
        x += w;
        if (i < breaks) {
            intervals.push({ t0: b.to, t1: bursts[i + 1].from, x0: x, x1: x + breakW, gap: true });
            x += breakW;
        }
    });
    const at = (t) => {
        if (!Number.isFinite(t) || t <= intervals[0].t0) return t === intervals[0].t0 && intervals[0].t1 === intervals[0].t0 ? (intervals[0].x0 + intervals[0].x1) / 2 : intervals[0].x0;
        for (const iv of intervals) {
            if (t > iv.t1) continue;
            return iv.t1 > iv.t0 ? iv.x0 + ((t - iv.t0) / (iv.t1 - iv.t0)) * (iv.x1 - iv.x0) : (iv.x0 + iv.x1) / 2;
        }
        return intervals[intervals.length - 1].x1;
    };
    const ticks = [];
    for (const iv of intervals) {
        if (iv.gap) continue;
        const px = iv.x1 - iv.x0;
        const inside = iv.t1 > iv.t0 ? axisTicks({ from: iv.t0, to: iv.t1 }, Math.max(1, Math.floor(px / 72))) : [iv.t0];
        for (const t of inside) {
            const tx = at(t);
            if (ticks.length && tx - ticks[ticks.length - 1].x < 52) continue;
            ticks.push({ t, x: tx });
        }
        // A burst too short for a round tick still says when it was.
        if (!inside.length) ticks.push({ t: iv.t0, x: iv.x0 });
    }
    const gapsOut = intervals.filter((iv) => iv.gap).map((iv) => ({ x0: iv.x0, x1: iv.x1, x: (iv.x0 + iv.x1) / 2, ms: iv.t1 - iv.t0 }));
    return { x: at, ticks, breaks: gapsOut, first, last, bursts: bursts.length };
}

// A label beside its mark: right of it when it fits, else left of it.
function placeLabel(markX, text, end, x0, x1) {
    const w = text.length * CHAR_W;
    const right = markX + 5;
    if (right >= end + 8 && right + w <= x1) return { x: right, anchor: "start", end: right + w };
    const left = markX - 5;
    if (left - w >= Math.max(x0, end + 8)) return { x: left, anchor: "end", end: left };
    return null;
}

/**
 * The chart for a timelineView at `width`: its size, the rail (lanes, tree,
 * nodes and people chips), the clock, marks and their labels, spans, windows,
 * the claims' badges and the hosts, and the stems joining them to marks.
 */
export function layoutTimeline(view, { width, labelMarks = true } = {}) {
    const W = Math.max(280, Math.floor(width || 0));
    const rail = Math.round(Math.min(LAYOUT.railMax, Math.max(LAYOUT.railMin, W * 0.24)));
    const hasUndated = view.groups.some((g) => g.rows.some((r) => r.undated));
    const plot = { x0: rail + 10, x1: W - 8 - (hasUndated ? LAYOUT.undatedW : 0) };

    const points = [];
    const events = new Map();
    const spansAt = new Map();
    for (const g of view.groups) {
        for (const r of g.rows) {
            for (const e of r.events) {
                points.push(e.at);
                events.set(e.key, e);
            }
            if (r.span) {
                points.push(r.span.from, r.span.to);
                spansAt.set(r.key, r.span);
            }
        }
    }
    if (!points.length) for (const w of view.windows) points.push(w.from, w.to);
    const scale = brokenScale(points, plot.x0 + 4, plot.x1 - 4);

    // Claims' badges: each at the earliest time it rests on, in the first row
    // of the band with room for it.
    const badges = [];
    const rowEnds = [];
    view.claims.slice(0, LAYOUT.maxBadges).forEach((c, i) => {
        const xs = [];
        for (const k of c.evidence) {
            const e = events.get(k);
            if (e && scale) xs.push(scale.x(e.at));
            const span = spansAt.get(k);
            if (span && scale) xs.push(scale.x(span.from));
        }
        const half = LAYOUT.badgeW / 2;
        let x = Math.min(plot.x1 - half, Math.max(plot.x0 + half, xs.length ? Math.min(...xs) : plot.x0 + half + i * (LAYOUT.badgeW + 4)));
        let row = rowEnds.findIndex((end) => x - half >= end + 3);
        if (row < 0 && rowEnds.length < LAYOUT.maxBadgeRows) row = rowEnds.length;
        if (row < 0) {
            // Every row is taken here: the badge moves right along the row that
            // ends first, beside the one before it rather than over it.
            row = rowEnds.indexOf(Math.min(...rowEnds));
            x = Math.min(plot.x1 - half, rowEnds[row] + 3 + half);
        }
        rowEnds[row] = Math.max(rowEnds[row] ?? -Infinity, x + half);
        badges.push({ key: `claim:${c.id}`, claimId: c.id, n: i + 1, x, y: LAYOUT.axisH + 4 + row * LAYOUT.badgeRowH + LAYOUT.badgeH / 2, isNew: Boolean(c.isNew), status: c.status, statement: c.statement });
    });
    const bandH = rowEnds.length ? rowEnds.length * LAYOUT.badgeRowH + 6 : 0;
    const lanesTop = LAYOUT.axisH + bandH;

    const lanes = [];
    const machineNodes = new Map();
    const laneNodes = new Map();
    const tree = [];
    const chips = [];
    let y = lanesTop;
    const addMachine = (g, bare) => {
        const lane = { type: "machine", key: g.key, y, h: LAYOUT.headH, label: g.label, isNew: Boolean(g.isNew), bare, group: g };
        lanes.push(lane);
        machineNodes.set(g.key, { x: 7, y: y + LAYOUT.headH / 2 });
        // The people seen on a machine sit beside it: seen-on is adjacency, not a line.
        let cx = plot.x0 + 4;
        const shown = [];
        for (const p of g.people || []) {
            const label = clip(p.label, 16);
            const w = label.length * CHAR_W + 8;
            if (cx + w > plot.x1 - 30) break;
            shown.push({ key: p.key, lane: g.key, label, full: p.label, x: cx, y: y + (LAYOUT.headH - LAYOUT.chipH) / 2, w, isNew: Boolean(p.isNew) });
            cx += w + 4;
        }
        chips.push(...shown);
        if ((g.people || []).length > shown.length) chips.push({ key: `${g.key}:more`, lane: g.key, more: g.people.length - shown.length, x: cx, y: y + (LAYOUT.headH - LAYOUT.chipH) / 2 });
        y += LAYOUT.headH;
        return lane;
    };
    let hidden = 0;
    for (const g of view.groups) {
        if (lanes.length + 1 + g.rows.length > LAYOUT.maxLanes && lanes.length) {
            // Its header lane is a lane not drawn too.
            hidden += g.rows.length + 1;
            continue;
        }
        const head = addMachine(g, false);
        const elbows = [];
        for (const r of g.rows) {
            const hostKeys = [...new Set(r.events.flatMap((e) => e.reachedKeys || []))];
            const hostRows = Math.min(hostKeys.length, LAYOUT.maxLaneHosts);
            lanes.push({ type: r.kind === "session" ? "session" : "own", key: r.key, y, h: LAYOUT.laneH + hostRows * LAYOUT.hostRowH, row: r, group: g, hostKeys });
            // The lane's node sits on its line, where the session's span runs.
            const node = { x: 19, y: y + LAYOUT.labelH + MARK_Y.prompt };
            laneNodes.set(r.key, node);
            elbows.push(node.y);
            y += lanes[lanes.length - 1].h;
        }
        if (elbows.length) tree.push({ x: 7, y0: head.y + LAYOUT.headH / 2 + 4, y1: elbows[elbows.length - 1], elbows });
    }
    if (hidden) {
        lanes.push({ type: "more", key: "lanes:more", y, h: 14, label: `+${hidden} more lanes not drawn` });
        y += 14;
    }
    const bare = view.machines.slice(0, LAYOUT.maxBare);
    for (const m of bare) addMachine(m, true);
    if (view.machines.length > bare.length) {
        lanes.push({ type: "more", key: "machines:more", y, h: 14, label: `+${view.machines.length - bare.length} more machines named, nothing from them in the graph yet` });
        y += 14;
    }
    const lanesBottom = y;

    const marks = [];
    const markOf = new Map();
    const labels = [];
    const spans = [];
    const notes = [];
    for (const lane of lanes) {
        if (!lane.row) continue;
        const node = laneNodes.get(lane.key);
        if (scale && lane.row.span) {
            spans.push({ key: lane.key, x0: scale.x(lane.row.span.from), x1: scale.x(lane.row.span.to), y: node.y, isNew: Boolean(lane.row.isNew) });
        }
        if (!lane.row.events.length) {
            const span = spans.find((sp) => sp.key === lane.key);
            const text = namedText(lane.row.named);
            const w = text.length * CHAR_W;
            const at = span && span.x1 + 6 + w <= plot.x1 ? { x: span.x1 + 6, anchor: "start" }
                : span && span.x0 - 6 - w >= plot.x0 ? { x: span.x0 - 6, anchor: "end" }
                    : { x: plot.x0 + 4, anchor: "start" };
            notes.push({ key: lane.key, ...at, y: node.y + 3, text });
            continue;
        }
        if (!scale) continue;
        const buckets = new Map();
        for (const e of lane.row.events) {
            const px = scale.x(e.at);
            const k = `${lane.key}|${e.kind}|${Math.round(px / LAYOUT.bucketPx)}`;
            let b = buckets.get(k);
            if (!b) {
                b = { key: k, lane: lane.key, kind: e.kind, x: px, y: lane.y + LAYOUT.labelH + (MARK_Y[e.kind] ?? MARK_Y.prompt), events: [], isNew: false };
                buckets.set(k, b);
            }
            b.events.push(e);
            if (e.isNew) b.isNew = true;
            markOf.set(e.key, b);
        }
        const laneMarks = [...buckets.values()].sort((a, b) => a.x - b.x);
        marks.push(...laneMarks);
        if (!labelMarks) continue;
        let end = -Infinity;
        for (const b of laneMarks) {
            // A mark whose events carry only their ids has nothing to say in a label.
            if (!b.isNew || !b.events[0].labelled) continue;
            const text = clip(b.events[0].label, 22) + (b.events.length > 1 ? ` +${b.events.length - 1}` : "");
            const at = placeLabel(b.x, text, end, plot.x0, plot.x1);
            if (!at) continue;
            labels.push({ key: b.key, lane: lane.key, x: at.x, anchor: at.anchor, y: lane.y + LAYOUT.labelH - 1, text });
            end = at.end;
        }
    }
    const windows = scale ? view.windows.map((w) => ({ key: w.key, from: w.from, to: w.to, x0: scale.x(w.from), x1: scale.x(w.to), isNew: Boolean(w.isNew), edgeLeft: w.from >= scale.first, edgeRight: w.to <= scale.last })) : [];

    // Hosts: under each lane, a row per host its processes reached: a bar
    // joining ticks from those marks, ending in the host's node and name.
    const hostLabel = new Map(view.hosts.map((h) => [h.key, h]));
    const hosts = [];
    const placedHosts = new Set();
    for (const lane of lanes) {
        if (!lane.hostKeys?.length) continue;
        const laneMarks = marks.filter((b) => b.lane === lane.key);
        lane.hostKeys.slice(0, LAYOUT.maxLaneHosts).forEach((hk, i) => {
            const from = laneMarks.filter((b) => b.events.some((e) => e.reachedKeys?.includes(hk)));
            if (!from.length) return;
            const h = hostLabel.get(hk) || { key: hk, label: hk, isNew: false };
            const x = Math.max(...from.map((b) => b.x)) + 8;
            const room = Math.max(4, Math.floor((plot.x1 - x - 8) / CHAR_W));
            hosts.push({ key: hk, lane: lane.key, x, y: lane.y + LAYOUT.laneH + 2 + i * LAYOUT.hostRowH, label: clip(h.label, Math.min(28, room)), full: h.label, isNew: Boolean(h.isNew), from });
            placedHosts.add(hk);
        });
    }
    // Hosts reached from no mark on the chart (a session's reach) are listed under it.
    const unplacedHosts = view.hosts.filter((h) => !placedHosts.has(h.key));

    // Stems: a claim's run down from its badge to each mark it rests on (a
    // session it rests on, to the nearest point of the session's line); a
    // host's run up to each process that reached it. A shared bar under the
    // badge (over the host) joins a node's stems, so one node reads as one.
    const combs = [];
    for (const bdg of badges) {
        const claim = view.claims.find((c) => c.id === bdg.claimId);
        const targets = [];
        const seen = new Set();
        for (const k of claim.evidence) {
            const m = markOf.get(k);
            if (m && !seen.has(m.key)) {
                seen.add(m.key);
                targets.push({ key: m.key, x: m.x, y: m.y - 4 });
                continue;
            }
            const span = spans.find((sp) => sp.key === k);
            if (span && !seen.has(k)) {
                seen.add(k);
                targets.push({ key: k, x: Math.min(span.x1, Math.max(span.x0, bdg.x)), y: span.y });
            }
        }
        if (!targets.length) continue;
        const bar = bdg.y + LAYOUT.badgeH / 2 + 3;
        const xs = [bdg.x, ...targets.map((t) => t.x)];
        combs.push({ kind: "cites", from: bdg.key, claimId: bdg.claimId, node: { x: bdg.x, y: bdg.y + LAYOUT.badgeH / 2 }, bar, x0: Math.min(...xs), x1: Math.max(...xs), stems: targets });
    }
    for (const h of hosts) {
        const targets = h.from.map((b) => ({ key: b.key, x: b.x, y: b.y + 3 }));
        const xs = [h.x, ...targets.map((t) => t.x)];
        combs.push({ kind: "reached", from: h.key, lane: h.lane, node: { x: h.x, y: h.y }, bar: h.y, x0: Math.min(...xs), x1: Math.max(...xs), stems: targets });
    }

    return {
        width: W,
        height: lanesBottom + 4,
        rail,
        plot,
        hasUndated,
        lanes,
        lanesTop,
        lanesBottom,
        tree,
        machineNodes,
        laneNodes,
        chips,
        scale,
        marks,
        markOf,
        labels,
        spans,
        notes,
        windows,
        badges,
        hosts,
        unplacedHosts,
        combs,
    };
}
